import { Component, inject, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { AiRestrictedHoverDirective } from 'src/app/shared/directives/ai-restricted-hover.directive';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CreateChunkOptionsOverlayData } from 'src/app/views/content/chunk/create-chunk/create-chunk-options-overlay/create-chunk-options-overlay.component';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { VideoRecorderDialogComponent } from './dialog/video-recorder-dialog.component';

@Component({
  selector: 'app-video-recorder',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, MatDialogModule, TranslateModule, AiRestrictedHoverDirective],
  template: `
    @switch (displayMode) {
      @case ('interactive') {
        <div
          class="interactive-item"
          [class.disabled]="!canUseAI"
          (click)="openVideoRecorder()"
          [matTooltip]="(canUseAI ? itemText : 'GenerateContent.NoAiAccessTooltip') | translate"
          [appAiRestrictedHover]="!canUseAI"
        >
          <div class="interactive-item__content">
            <div class="interactive-item__prefix"></div>
            <mat-icon class="interactive-item__icon" aria-hidden="true">{{ itemIcon }}</mat-icon>
            <span class="interactive-item__text">{{ itemText | translate }}</span>
          </div>
        </div>
      }
      @case ('icon') {
        <span [matTooltip]="(canUseAI ? itemText : 'GenerateContent.NoAiAccessTooltip') | translate" [appAiRestrictedHover]="!canUseAI">
          <button mat-icon-button (click)="openVideoRecorder()" [disabled]="!canUseAI">
            <mat-icon>{{ itemIcon }}</mat-icon>
          </button>
        </span>
      }
    }
  `,
})
export class VideoRecorderComponent {
  @Input() displayMode: 'interactive' | 'icon' = 'interactive';
  @Input() data!: CreateChunkOptionsOverlayData;

  itemText = 'VideoRecorder.Tooltip.Record';
  itemIcon = 'cinematic_blur';

  private authService = inject(AuthService);
  canUseAI = this.authService.canUseAI();

  private dialog = inject(MatDialog);
  private contentStateService = inject(ContentStateService);
  private overlayService = inject(DynamicOverlayService);
  private permissionWatcher = inject(MediaDevicePermissionWatcherService);
  private toast = inject(ToastService);

  async openVideoRecorder(): Promise<void> {
    if (!this.canUseAI) return;

    const cameraAccess = this.permissionWatcher.cameraPermissionState;
    const micAccess = this.permissionWatcher.microphonePermissionState;

    if (cameraAccess === 'prompt' || micAccess === 'prompt') {
      const granted = await this.permissionWatcher.requestCameraAndMicrophoneAccess();
      if (!granted) {
        return;
      }
    } else if (cameraAccess === 'denied' || micAccess === 'denied') {
      this.toast.showDevicePermissionError();
      return;
    }

    this.overlayService.close();
    const dialogRef = this.dialog.open(VideoRecorderDialogComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'full-screen-dialog',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: Blob | undefined) => {
      if (result instanceof Blob) {
        this.contentStateService.uploadRecording(result, 'video', this.data?.chunkAboveId);
      }
    });
  }
}
